/**
 * Repeatable Character Design & Micro-Animated Portrait System
 *
 * Implements persistent, repeatable visual identities for the cast of "The Caesura Engine":
 * 1. Marcus Ward (54) — Senior Jump Diagnostics Engineer (Optic nerve fibrosis, neuro-lattice collar)
 * 2. Elena Cruz (42) — Chief Jump Architect (Cybernetic ocular HUD monocle, severe silhouette)
 * 3. Dr. Simon Peter (61) — Chief Medical Officer (Amber anti-radiation spectacles, Chronozepine vials)
 * 4. Cadet Silas Vance (22) — Junior Combine Apprentice (Anxious youth, pristine Combine patch)
 * 5. Inquisitor Thorne (48) — High Tribunal Corporate Interrogator (Brutalist obsidian uniform)
 * 6. The Spectator in the Margin — Non-Euclidean geometric silhouette lurking in the Planck gap
 */

import { CharacterId, CharacterState, CharacterProfile } from '../types/character';

export const CHARACTER_PROFILES: Record<CharacterId, CharacterProfile> = {
  marcus_ward: {
    id: 'marcus_ward',
    name: 'Marcus Ward',
    role: 'Senior Jump Diagnostics Engineer',
    affiliation: 'Thorne-Vance Deep Transit Combine // USSC Geryon',
    age: 54,
    physicalDescription: 'Weathered jawline with salt-and-pepper stubble, hollowed exhaustion beneath dark eyes, scarred optic nerves with 40Hz micro-saccades, grease-stained Combine jumpsuit with brass collar latches, neuro-lattice neck interface.',
    psychEvaluation: 'Stage-II Chronognostic Aphasia. High cognitive fatigue. Persistent sensory bleed from unrendered coordinate margins.',
    primaryColor: '#00f3ff',
    secondaryColor: '#ff2a4b',
    states: ['neutral', 'weary', 'synaptic_bleed', 'stasis_frost', 'in_transit'],
  },
  elena_cruz: {
    id: 'elena_cruz',
    name: 'Elena Cruz',
    role: 'Chief Jump Architect & Coordinate Theorist',
    affiliation: 'Thorne-Vance Navigation Guild',
    age: 42,
    physicalDescription: 'Sharp angular facial structure, severe asymmetric crop, cybernetic brass ocular monocle over right eye projecting cyan telemetry, high-collared midnight navy Combine officer tunic with silver vector pins.',
    psychEvaluation: 'Obsessive mathematical fixations. Denies perceptual drift. Unyielding commitment to transit schedules.',
    primaryColor: '#e040fb',
    secondaryColor: '#00f3ff',
    states: ['neutral', 'weary', 'synaptic_bleed', 'in_transit', 'interrogation'],
  },
  dr_simon_peter: {
    id: 'dr_simon_peter',
    name: 'Dr. Simon Peter',
    role: 'Chief Medical Officer & Neuro-Pathologist',
    affiliation: 'Combine Maritime Medical Service',
    age: 61,
    physicalDescription: 'Receding gray hair, wireframe spectacles with amber blue-light filtration lenses, creased brow of chronic worry, disheveled cream thermal medical coat with pneumatic Chronozepine injector holsters.',
    psychEvaluation: 'Clinically despondent. Secretly hoarding Chronozepine to mitigate crew cognitive breakdown.',
    primaryColor: '#ffb700',
    secondaryColor: '#00e5ff',
    states: ['neutral', 'weary', 'stasis_frost', 'interrogation'],
  },
  cadet_silas: {
    id: 'cadet_silas',
    name: 'Cadet Silas Vance',
    role: 'Junior Drive Technician (First Voyage)',
    affiliation: 'Perseus Technical Academy // Combine Indentured',
    age: 22,
    physicalDescription: 'Youthful unlined face, wide anxious pupils reflecting emergency warning diodes, pristine unsoiled cadet flight coveralls with yellow apprenticeship epaulets, trembling fingers.',
    psychEvaluation: 'Acute panic reaction on first encounter with the unrendered Planck gap.',
    primaryColor: '#ffea00',
    secondaryColor: '#ff5252',
    states: ['neutral', 'weary', 'stasis_frost', 'synaptic_bleed'],
  },
  inquisitor_thorne: {
    id: 'inquisitor_thorne',
    name: 'Inquisitor Vane Thorne',
    role: 'High Commercial Tribunal Investigator',
    affiliation: 'Combine Executive Legal Directorate',
    age: 48,
    physicalDescription: 'Slicked obsidian hair, aristocratic predator cheekbones, sharp sterile gaze, tailored matte-black ballistic wool trench coat with crimson Combine seal crest, sub-dermal audio-recorder blinking red.',
    psychEvaluation: 'Emotionally detached. Treats crew lives as actuarial loss parameters.',
    primaryColor: '#ff1744',
    secondaryColor: '#9e9e9e',
    states: ['neutral', 'interrogation'],
  },
  margin_spectator: {
    id: 'margin_spectator',
    name: 'The Spectator in the Margin',
    role: 'Extra-Temporal Observer',
    affiliation: 'The Unrendered Gap (Planck Continuum)',
    physicalDescription: 'Crystalline non-Euclidean silhouette with shifting geometric facets, dual horizontal slit apertures of blinding cold violet luminescence, observing without breathing or blinking.',
    psychEvaluation: 'Incalculable. Exists exclusively within the infinitesimal 5.39e-44 second interval.',
    primaryColor: '#aa00ff',
    secondaryColor: '#ffffff',
    states: ['neutral', 'synaptic_bleed'],
  },
};

export function getCharacterProfile(id: CharacterId): CharacterProfile {
  return CHARACTER_PROFILES[id] || CHARACTER_PROFILES.marcus_ward;
}

/**
 * Generates a persistent, repeatable, micro-animated SVG character portrait
 */
export function generateCharacterPortrait(
  charId: CharacterId,
  state: CharacterState = 'neutral',
  options: { width?: number; height?: number; subtitle?: string } = {}
): string {
  const profile = getCharacterProfile(charId);
  const { width = 400, height = 240, subtitle } = options;
  const pColor = profile.primaryColor;
  const sColor = profile.secondaryColor;

  // Specific repeatable facial artwork per character
  let faceElements = '';

  switch (charId) {
    case 'marcus_ward': {
      const isBleeding = state === 'synaptic_bleed';
      const isFrost = state === 'stasis_frost';
      const eyeR = isBleeding ? '#ff2a4b' : '#00f3ff';
      const pupilGlow = isBleeding ? 'filter="url(#retinalGlow)"' : '';

      faceElements = `
        <!-- Shoulders / Mechanic Jumpsuit -->
        <polygon points="110,240 135,170 265,170 290,240" fill="#141c24" stroke="#253545" stroke-width="2"/>
        <!-- Heavy Brass Collar Clasps -->
        <rect x="180" y="165" width="40" height="25" rx="3" fill="#0c1218" stroke="#ffb700" stroke-width="1.2"/>
        <line x1="200" y1="165" x2="200" y2="190" stroke="#ffb700" stroke-width="1"/>
        <!-- Combine Patch on Left Shoulder -->
        <rect x="120" y="195" width="28" height="16" rx="2" fill="#080c10" stroke="#00f3ff" stroke-width="0.8"/>
        <text x="124" y="206" fill="#00f3ff" font-family="monospace" font-size="6.5">0x992B</text>
        <!-- Neck & Neuro-Lattice Spinal Implant -->
        <rect x="185" y="130" width="30" height="40" fill="#8d7362"/>
        <line x1="185" y1="145" x2="215" y2="145" stroke="${eyeR}" stroke-width="1.5" stroke-dasharray="3,2" class="neuro-port-pulse"/>
        <circle cx="212" cy="145" r="2.5" fill="${eyeR}"/>
        <!-- Head & Weary Jaw -->
        <polygon points="160,70 240,70 230,140 200,152 170,140" fill="#b09380" stroke="#5a4536" stroke-width="1.5"/>
        <!-- Graying Stubble & Jawline -->
        <path d="M 172 120 Q 200 152 228 120" fill="none" stroke="#4a3e35" stroke-width="4" stroke-dasharray="2,3"/>
        <!-- Eyes & Optic Nerve Scarring -->
        <ellipse cx="185" cy="98" rx="8" ry="4.5" fill="#121820" stroke="#4a3b30" stroke-width="1"/>
        <ellipse cx="215" cy="98" rx="8" ry="4.5" fill="#121820" stroke="#4a3b30" stroke-width="1"/>
        <!-- Retinal Micro-Saccade Pupil Glow -->
        <circle cx="185" cy="98" r="3" fill="${eyeR}" ${pupilGlow} class="optic-retina-jitter"/>
        <circle cx="215" cy="98" r="3" fill="${eyeR}" ${pupilGlow} class="optic-retina-jitter"/>
        <!-- Heavy Brow of Exhaustion -->
        <line x1="175" y1="90" x2="195" y2="92" stroke="#403227" stroke-width="2.5"/>
        <line x1="205" y1="92" x2="225" y2="90" stroke="#403227" stroke-width="2.5"/>
        <!-- Salt & Pepper Disheveled Hair -->
        <polygon points="155,75 200,45 245,75 235,55 200,38 165,55" fill="#4d555c" stroke="#373d42" stroke-width="1.5"/>
        ${isFrost ? '<path d="M 160 55 L 240 55" stroke="#00e5ff" stroke-width="3" stroke-dasharray="2,4" opacity="0.8"/>' : ''}
      `;
      break;
    }

    case 'elena_cruz': {
      faceElements = `
        <!-- High Officer Tunic -->
        <polygon points="120,240 145,165 255,165 280,240" fill="#0d1b2a" stroke="#1b3a5c" stroke-width="2"/>
        <line x1="200" y1="165" x2="200" y2="240" stroke="#00f3ff" stroke-width="1"/>
        <!-- Silver Vector Epaulets -->
        <polygon points="140,168 160,165 145,185" fill="#silver" stroke="#00f3ff" stroke-width="1"/>
        <polygon points="260,168 240,165 255,185" fill="#silver" stroke="#00f3ff" stroke-width="1"/>
        <!-- Slender Neck -->
        <rect x="188" y="128" width="24" height="40" fill="#caa592"/>
        <!-- Sharp Angular Face -->
        <polygon points="165,65 235,65 225,135 200,150 175,135" fill="#e2c0af" stroke="#775647" stroke-width="1.2"/>
        <!-- Natural Left Eye -->
        <ellipse cx="182" cy="95" rx="7" ry="4" fill="#151b22" stroke="#5a3d31" stroke-width="1"/>
        <circle cx="182" cy="95" r="2.8" fill="#442a1d"/>
        <line x1="174" y1="88" x2="192" y2="88" stroke="#221914" stroke-width="2"/>
        <!-- Cybernetic Ocular Monocle (Right Eye) -->
        <circle cx="218" cy="95" r="9" fill="#06121e" stroke="#00f3ff" stroke-width="1.8" class="cyber-monocle-pulse"/>
        <circle cx="218" cy="95" r="4" fill="#00f3ff"/>
        <line x1="209" y1="95" x2="227" y2="95" stroke="#00f3ff" stroke-width="0.75"/>
        <line x1="218" y1="86" x2="218" y2="104" stroke="#00f3ff" stroke-width="0.75"/>
        <line x1="227" y1="95" x2="238" y2="95" stroke="#00f3ff" stroke-width="1.5"/>
        <!-- Severe Asymmetric Black Crop Hair -->
        <polygon points="158,70 190,40 240,55 240,95 235,70 195,50 155,90" fill="#111417" stroke="#000" stroke-width="1.5"/>
      `;
      break;
    }

    case 'dr_simon_peter': {
      faceElements = `
        <!-- Cream Thermal Medical Smock -->
        <polygon points="115,240 140,165 260,165 285,240" fill="#2c2a26" stroke="#48443c" stroke-width="2"/>
        <rect x="155" y="165" width="90" height="75" fill="#1a1815"/>
        <!-- Chronozepine Injector Vials in Holster -->
        <rect x="145" y="195" width="6" height="24" rx="2" fill="#00e5ff" stroke="#ffffff" stroke-width="0.75"/>
        <rect x="155" y="195" width="6" height="24" rx="2" fill="#00e5ff" stroke="#ffffff" stroke-width="0.75"/>
        <!-- Neck -->
        <rect x="187" y="130" width="26" height="38" fill="#bc9885"/>
        <!-- Older Face with Creased Forehead -->
        <polygon points="165,68 235,68 228,138 200,148 172,138" fill="#c9a794" stroke="#775a49" stroke-width="1.2"/>
        <!-- Wireframe Amber Anti-Radiation Spectacles -->
        <rect x="172" y="90" width="22" height="16" rx="2" fill="rgba(255, 183, 0, 0.25)" stroke="#ffb700" stroke-width="1.2"/>
        <rect x="206" y="90" width="22" height="16" rx="2" fill="rgba(255, 183, 0, 0.25)" stroke="#ffb700" stroke-width="1.2"/>
        <line x1="194" y1="96" x2="206" y2="96" stroke="#ffb700" stroke-width="1.5"/>
        <line x1="168" y1="96" x2="172" y2="96" stroke="#ffb700" stroke-width="1.2"/>
        <line x1="228" y1="96" x2="232" y2="96" stroke="#ffb700" stroke-width="1.2"/>
        <!-- Tired Pupils Behind Lenses -->
        <circle cx="183" cy="98" r="2.5" fill="#3e2723"/>
        <circle cx="217" cy="98" r="2.5" fill="#3e2723"/>
        <!-- Receding Graying Hair -->
        <path d="M 165 75 Q 200 48 235 75 Q 220 55 200 52 Q 180 55 165 75" fill="#78909c"/>
      `;
      break;
    }

    case 'cadet_silas': {
      faceElements = `
        <!-- Pristine Cadet Flight Jumpsuit -->
        <polygon points="120,240 145,170 255,170 280,240" fill="#182230" stroke="#ffea00" stroke-width="1.5"/>
        <!-- Bright Yellow Apprenticeship Epaulets -->
        <rect x="140" y="170" width="22" height="8" rx="2" fill="#ffea00"/>
        <rect x="238" y="170" width="22" height="8" rx="2" fill="#ffea00"/>
        <rect x="190" y="130" width="20" height="42" fill="#dfbba5"/>
        <!-- Youthful Rounder Face -->
        <polygon points="168,70 232,70 225,135 200,146 175,135" fill="#eccbb7" stroke="#8d6e63" stroke-width="1"/>
        <!-- Wide Terrified Eyes -->
        <ellipse cx="182" cy="96" rx="8" ry="7" fill="#ffffff" stroke="#333" stroke-width="1"/>
        <ellipse cx="218" cy="96" rx="8" ry="7" fill="#ffffff" stroke="#333" stroke-width="1"/>
        <circle cx="182" cy="96" r="3.5" fill="#212121"/>
        <circle cx="218" cy="96" r="3.5" fill="#212121"/>
        <!-- High Raised Anxious Eyebrows -->
        <line x1="174" y1="84" x2="190" y2="82" stroke="#4e342e" stroke-width="2"/>
        <line x1="210" y1="82" x2="226" y2="84" stroke="#4e342e" stroke-width="2"/>
        <!-- Neat Brown Cadet Haircut -->
        <path d="M 164 72 Q 200 40 236 72 Z" fill="#4e342e"/>
      `;
      break;
    }

    case 'inquisitor_thorne': {
      faceElements = `
        <!-- Brutalist High-Collar Obsidian Trench Coat -->
        <polygon points="110,240 135,160 265,160 290,240" fill="#0a0a0c" stroke="#ff1744" stroke-width="1.8"/>
        <!-- High Stiff Stand-up Collar -->
        <polygon points="175,160 165,120 235,120 225,160" fill="#000000" stroke="#ff1744" stroke-width="1.2"/>
        <circle cx="200" cy="180" r="6" fill="#ff1744"/>
        <!-- Pale Sharp Predator Face -->
        <polygon points="170,60 230,60 222,125 200,140 178,125" fill="#cfd8dc" stroke="#37474f" stroke-width="1.2"/>
        <!-- Cold Slit Eyes -->
        <line x1="176" y1="88" x2="192" y2="88" stroke="#ff1744" stroke-width="1.8"/>
        <line x1="208" y1="88" x2="224" y2="88" stroke="#ff1744" stroke-width="1.8"/>
        <!-- Sub-dermal Red Audio Earpiece Pinpoint -->
        <circle cx="230" cy="95" r="2.5" fill="#ff1744" class="inquest-beacon-blink"/>
        <!-- Slicked Black Hair -->
        <polygon points="166,62 200,38 234,62 230,48 200,32 170,48" fill="#121214"/>
      `;
      break;
    }

    case 'margin_spectator': {
      faceElements = `
        <!-- Non-Euclidean Shifting Geometry -->
        <polygon points="200,30 250,90 230,170 170,170 150,90" fill="#120422" stroke="#aa00ff" stroke-width="2.5" class="spectator-poly-breathe"/>
        <polygon points="200,45 235,95 220,155 180,155 165,95" fill="none" stroke="#e040fb" stroke-width="1" stroke-dasharray="4,4"/>
        <!-- Dual Horizontal Slit Eyes of Timeless Stillness -->
        <line x1="175" y1="95" x2="195" y2="95" stroke="#ffffff" stroke-width="2.5" class="optic-slit-glow"/>
        <line x1="205" y1="95" x2="225" y2="95" stroke="#ffffff" stroke-width="2.5" class="optic-slit-glow"/>
        <!-- Shifting Crystalline Coordinate Vectors -->
        <line x1="150" y1="90" x2="250" y2="90" stroke="#00f3ff" stroke-width="0.8" opacity="0.6"/>
        <line x1="200" y1="30" x2="200" y2="170" stroke="#00f3ff" stroke-width="0.8" opacity="0.6"/>
        <!-- Body Dissolves Into Negative Space -->
        <polygon points="170,170 230,170 260,240 140,240" fill="#08020e" stroke="#aa00ff" stroke-width="1.5" stroke-opacity="0.5"/>
      `;
      break;
    }
  }

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="100%" height="100%">
  <defs>
    <radialGradient id="portrait_bg_${charId}" cx="50%" cy="40%" r="75%">
      <stop offset="0%" stop-color="#0c1420"/>
      <stop offset="55%" stop-color="#05080e"/>
      <stop offset="100%" stop-color="#010204"/>
    </radialGradient>
    <filter id="retinalGlow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="2.5" result="blur"/>
      <feMerge>
        <feMergeNode in="blur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>

  <style>
    .neuro-port-pulse { animation: portPulse 2s ease-in-out infinite alternate; }
    .optic-retina-jitter { animation: retinalJitter 0.08s steps(2, start) infinite; }
    .cyber-monocle-pulse { animation: monocleGlow 2.5s ease-in-out infinite alternate; }
    .inquest-beacon-blink { animation: inquestBlink 1.2s step-end infinite; }
    .spectator-poly-breathe { animation: specBreathe 4s ease-in-out infinite alternate; }
    .optic-slit-glow { animation: slitGlow 3s ease-in-out infinite alternate; }
    @keyframes portPulse { 0% { opacity: 0.4; } 100% { opacity: 1; } }
    @keyframes retinalJitter { 0% { transform: translate(0, 0); } 50% { transform: translate(0.6px, -0.4px); } }
    @keyframes monocleGlow { 0% { stroke-opacity: 0.5; } 100% { stroke-opacity: 1; } }
    @keyframes inquestBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
    @keyframes specBreathe { 0% { transform: scale(0.98); transform-origin: 200px 100px; } 100% { transform: scale(1.02); transform-origin: 200px 100px; } }
    @keyframes slitGlow { 0% { stroke: #e040fb; } 100% { stroke: #ffffff; } }
  </style>

  <rect width="${width}" height="${height}" fill="url(#portrait_bg_${charId})"/>

  <!-- Telemetry Scanline Backdrop -->
  <g stroke="${pColor}" stroke-opacity="0.08" stroke-width="0.5">
    ${Array.from({ length: 12 }, (_, i) => `<line x1="0" y1="${i * 20}" x2="${width}" y2="${i * 20}"/>`).join('')}
  </g>

  <!-- Repeatable Character Figure -->
  <g class="character-figure-group">
    ${faceElements}
  </g>

  <!-- Frame HUD Overlay & ID Metadata -->
  <g font-family="monospace" font-size="8" fill="${pColor}">
    <rect x="12" y="12" width="6" height="6" fill="none" stroke="${pColor}" stroke-width="1"/>
    <text x="24" y="18" font-weight="bold">${profile.name.toUpperCase()}</text>
    <text x="24" y="28" fill="#6b7c96" font-size="7">${profile.role.toUpperCase()}</text>
  </g>

  <!-- State & Affiliation Bottom Strip -->
  <g font-family="monospace" font-size="7.5" fill="#6b7c96">
    <text x="18" y="222" fill="${sColor}">STATE: ${state.toUpperCase().replace(/_/g, ' ')}</text>
    <text x="18" y="232">${subtitle || profile.affiliation.toUpperCase()}</text>
  </g>
</svg>
  `;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
